import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUSES,
  ORDER_KIND_LABELS,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUSES,
  SELLER_KIND_LABELS,
  COMMISSION_CATEGORY_LABELS,
  COUNTRY_NAMES,
  LICENSE_STATUS_LABELS,
  PLAN_LABELS,
  type Plan,
  territoryLabel,
  type PortfolioClientDTO,
  type CommissionCircuit,
  type CommissionPageDTO,
  type CommissionStatus,
  type Paged,
  type PayoutDTO,
  type ProspectDTO,
  type ProspectStatus,
  type SellerPortalDTO,
  type TeamMemberSummaryDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  Kpi,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import {
  BadgeDollarSign,
  CircleDollarSign,
  Clock,
  Columns3,
  Copy,
  Ellipsis,
  Link2,
  List,
  Pencil,
  Plus,
  Store,
  Target,
  Trash2,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { ProspectBoard } from '../components/ProspectBoard';
import {
  CommissionStatusBadge,
  copy,
  LostDialog,
  Pager,
  ProspectDialog,
  ProspectStatusBadge,
  prospectWhat,
  RangeFilter,
  useRange,
} from '../components/sellers';
import { api, errorMessage } from '../lib/api';
import { firstName, formatDate, formatUsd } from '../lib/format';
import { PayoutsTable } from './CommissionsPage';

const usePortal = () => useQuery({ queryKey: ['portal'], queryFn: () => api.get<SellerPortalDTO>('/platform/portal') });

/** Portal del vendedor o partner: su link, sus números y cómo cobra. */
export function PortalHomePage() {
  const toast = useToast();
  const portal = usePortal();
  if (portal.isPending) return <Skeleton style={{ height: 240 }} />;
  if (!portal.data) return <Callout tone="danger">No se pudo cargar tu panel.</Callout>;
  const { seller, own, team, territory } = portal.data;
  return (
    <>
      <PageHeader
        title={`Hola, ${firstName(seller.name)}`}
        subtitle={`${SELLER_KIND_LABELS[seller.kind]} de BifeAI${seller.leaderName ? ` · equipo de ${seller.leaderName}` : ''}`}
        actions={
          <Link to="/mis-prospectos?nuevo=1">
            <Button variant="primary" icon={<Plus />}>
              Cargar prospecto
            </Button>
          </Link>
        }
      />
      <Card>
        <CardHeader title="Tu link" subtitle="Las compras que se hagan en bifeai.com entrando por tu link se te asignan solas." />
        <CardBody>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Input readOnly value={seller.refUrl} onFocus={(e) => e.target.select()} style={{ flex: '1 1 260px' }} />
            <Button
              icon={<Copy />}
              onClick={() => void copy(seller.refUrl).then((ok) => (ok ? toast.success('Link copiado') : toast.error('No se pudo copiar')))}
            >
              Copiar
            </Button>
          </div>
        </CardBody>
      </Card>
      <div style={{ height: 18 }} />
      {team && (
        <h2 className="bf-card-title" style={{ margin: '0 0 10px' }}>
          Mis ventas
        </h2>
      )}
      <div className="bf-kpi-grid">
        <Kpi label="Comisiones a cobrar" tone="amber" icon={<Clock />} value={formatUsd(own.pendingCents)} delta="Pendientes de pago" deltaMuted />
        <Kpi label="Comisiones cobradas" tone="green" icon={<CircleDollarSign />} value={formatUsd(own.paidCents)} />
        <Kpi label="Mis clientes" icon={<Store />} value={own.clients} />
        <Kpi label="Prospectos abiertos" icon={<Target />} value={own.prospects} />
      </div>
      {team && (
        <>
          <div style={{ height: 18 }} />
          <h2 className="bf-card-title" style={{ margin: '0 0 10px' }}>
            Ventas de mi red
          </h2>
          <div className="bf-kpi-grid">
            <Kpi label="A cobrar por mi red" tone="amber" icon={<Clock />} value={formatUsd(team.pendingCents)} />
            <Kpi label="Cobrado por mi red" tone="green" icon={<CircleDollarSign />} value={formatUsd(team.paidCents)} />
            <Kpi label="Clientes de mi red" icon={<Store />} value={team.clients} />
            <Kpi label="Personas en mi red" icon={<Users />} value={team.members} />
          </div>
        </>
      )}
      {territory && (
        <>
          <div style={{ height: 18 }} />
          <h2 className="bf-card-title" style={{ margin: '0 0 10px' }}>
            Mi territorio: {territoryLabel(seller.territory)}
            {seller.exclusive ? ' (exclusivo)' : ''}
          </h2>
          <div className="bf-kpi-grid">
            <Kpi label="Clientes en mi territorio" icon={<Store />} value={territory.clients} />
            {seller.territoryRatePct !== null && (
              <>
                <Kpi label="A cobrar por el territorio" tone="amber" icon={<Clock />} value={formatUsd(territory.pendingCents)} />
                <Kpi label="Cobrado por el territorio" tone="green" icon={<CircleDollarSign />} value={formatUsd(territory.paidCents)} />
              </>
            )}
          </div>
        </>
      )}
      <div style={{ height: 18 }} />
      <Card>
        <CardHeader title="Cómo cobrás" />
        <CardBody>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
            {seller.rates.map((r) => (
              <li key={r.category}>
                {COMMISSION_CATEGORY_LABELS[r.category]}: <strong>{r.pct} %</strong> de lo que paga el cliente, en dólares
                {r.category === 'RENEWAL' && seller.activeMonths ? `, mientras hayas vendido en los últimos ${seller.activeMonths} meses` : ''}.
              </li>
            ))}
            {seller.territoryRatePct !== null && (
              <li>
                Ventas de tu territorio que no hace tu red: <strong>{seller.territoryRatePct} %</strong>.
              </li>
            )}
            {seller.agreementTo && <li>Tu acuerdo está vigente hasta el {formatDate(seller.agreementTo)}.</li>}
            {seller.isLeader && seller.teamRatePct !== null && (
              <li>
                Ventas de tu red: <strong>{seller.teamRatePct} %</strong> en total; vos te quedás con la diferencia con el % de cada persona.
                {seller.teamPayMode === 'LEADER'
                  ? ' BifeAI te paga a vos todo lo del equipo y vos le pagás a cada uno.'
                  : ' BifeAI le paga a cada uno lo suyo.'}
              </li>
            )}
            <li>Si te interesa un cliente, cargalo como prospecto: BifeAI le arma el link de pago y, cuando paga, la venta queda a tu nombre.</li>
            <li>Si una venta se devuelve, su comisión se anula.</li>
          </ul>
        </CardBody>
      </Card>
    </>
  );
}

export function PortalProspectsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const portal = usePortal();
  const r = useRange();
  const [scope, setScope] = useState<'own' | 'team'>('own');
  const [status, setStatus] = useState<ProspectStatus | ''>('');
  const [editing, setEditing] = useState<ProspectDTO | 'new' | null>(() => (new URLSearchParams(location.search).get('nuevo') ? 'new' : null));
  const [losing, setLosing] = useState<ProspectDTO | null>(null);
  const [view, setView] = useState<'board' | 'list'>('board');
  const list = useQuery({
    queryKey: ['portal', 'prospects', scope, r.qs({ status })],
    queryFn: () => api.get<Paged<ProspectDTO>>(`/platform/portal/prospects?${r.qs({ status, scope })}`),
    enabled: view === 'list',
  });
  const board = useQuery({
    queryKey: ['portal', 'prospects', 'board', scope],
    queryFn: () => api.get<Paged<ProspectDTO>>(`/platform/portal/prospects?board=1&scope=${scope}`),
    enabled: view === 'board',
    refetchInterval: 30_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal'] });
  const move = (p: ProspectDTO, status: 'NEW' | 'CONTACTED' | 'DEMO') =>
    void api
      .post(`/platform/portal/prospects/${p.id}/stage`, { status })
      .then(refresh)
      .catch((err: unknown) => toast.error('No se pudo mover', errorMessage(err)));
  const d = list.data;
  const mine = scope === 'own';

  return (
    <>
      <PageHeader
        title="Prospectos"
        subtitle="Tus posibles clientes. Arrastralos de etapa a medida que avanzás; cuando BifeAI arma el link de pago, lo ves acá para mandárselo."
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button icon={view === 'board' ? <List /> : <Columns3 />} onClick={() => setView(view === 'board' ? 'list' : 'board')}>
              {view === 'board' ? 'Ver lista' : 'Ver tablero'}
            </Button>
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              Cargar prospecto
            </Button>
          </div>
        }
      />
      {portal.data?.seller.isLeader && (
        <div className="bf-view-switch" role="tablist" aria-label="De quién" style={{ marginBottom: 16 }}>
          <button type="button" role="tab" aria-selected={mine} onClick={() => setScope('own')}>
            Míos
          </button>
          <button type="button" role="tab" aria-selected={!mine} onClick={() => setScope('team')}>
            De mi equipo
          </button>
        </div>
      )}
      {view === 'board' &&
        (board.isPending ? (
          <Skeleton style={{ height: 320 }} />
        ) : (
          <ProspectBoard
            items={board.data?.items ?? []}
            admin={false}
            showSeller={!mine}
            onOpen={(p) => mine && p.status !== 'WON' && setEditing(p)}
            onStage={(p, s) => (mine ? move(p, s) : toast.error('Solo el vendedor a cargo lo mueve'))}
            onLost={(p) => (mine ? setLosing(p) : toast.error('Solo el vendedor a cargo lo mueve'))}
            canDelete={(p) => mine && ['NEW', 'CONTACTED', 'DEMO'].includes(p.status)}
            onDelete={(p) =>
              api
                .del(`/platform/portal/prospects/${p.id}`)
                .then(refresh)
                .catch((err: unknown) => toast.error('No se pudo borrar', errorMessage(err)))
            }
          />
        ))}
      {view === 'list' && (
        <Card>
          <CardHeader
            title={
              <RangeFilter r={r}>
                <Select
                  aria-label="Estado"
                  style={{ width: 'auto' }}
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as ProspectStatus | '');
                    r.setPage(1);
                  }}
                >
                  <option value="">Todos los estados</option>
                  {PROSPECT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {PROSPECT_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </RangeFilter>
            }
          />
          <CardBody flush>
            {list.isPending ? (
              <Skeleton style={{ height: 160 }} />
            ) : !d?.items.length ? (
              <EmptyState
                icon={<Target />}
                title="Sin prospectos"
                text={mine ? 'Cargá tu primer posible cliente.' : 'Tu equipo todavía no cargó prospectos.'}
              />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Posible cliente</th>
                      <th>Quiere</th>
                      {!mine && <th>Vendedor</th>}
                      <th>Estado</th>
                      {mine && <th aria-label="Acciones" />}
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((p) => (
                      <tr key={p.id}>
                        <td className="bf-cell-sub">{formatDate(p.createdAt)}</td>
                        <td>
                          <span className="bf-cell-main">{p.business}</span>
                          <div className="bf-cell-sub">{[p.contactName, p.email, p.phone].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td>
                          <span className="bf-cell-sub">{prospectWhat(p)}</span>
                          <div className="bf-cell-sub">Hoy {formatUsd(p.todayCents)}</div>
                        </td>
                        {!mine && <td className="bf-cell-sub">{p.sellerName}</td>}
                        <td>
                          <ProspectStatusBadge status={p.status} />
                          {p.lostReason && <div className="bf-cell-sub">{p.lostReason}</div>}
                          {p.checkoutUrl && (
                            <div>
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<Link2 />}
                                onClick={() =>
                                  void copy(p.checkoutUrl!).then((ok) =>
                                    ok ? toast.success('Link de pago copiado', 'Mandáselo al cliente.') : toast.error('No se pudo copiar'),
                                  )
                                }
                              >
                                Copiar link de pago
                              </Button>
                            </div>
                          )}
                        </td>
                        {mine && (
                          <td style={{ textAlign: 'right' }}>
                            {p.status !== 'WON' && (
                              <Menu>
                                <MenuTrigger asChild>
                                  <Button size="sm" variant="ghost" aria-label={`Acciones de ${p.business}`} icon={<Ellipsis />} />
                                </MenuTrigger>
                                <MenuContent>
                                  <MenuItem icon={<Pencil />} onSelect={() => setEditing(p)}>
                                    Editar
                                  </MenuItem>
                                  {p.status !== 'LOST' && (
                                    <MenuItem icon={<XCircle />} onSelect={() => setLosing(p)}>
                                      Marcar perdido
                                    </MenuItem>
                                  )}
                                  {['NEW', 'CONTACTED', 'DEMO'].includes(p.status) && (
                                    <MenuItem
                                      icon={<Trash2 />}
                                      danger
                                      onSelect={() =>
                                        void api
                                          .del(`/platform/portal/prospects/${p.id}`)
                                          .then(refresh)
                                          .catch((err: unknown) => toast.error('No se pudo borrar', errorMessage(err)))
                                      }
                                    >
                                      Borrar
                                    </MenuItem>
                                  )}
                                </MenuContent>
                              </Menu>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {view === 'list' && d && <Pager page={d.page} total={d.total} pageSize={d.pageSize} onPage={r.setPage} />}
      {editing && (
        <ProspectDialog
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing === 'new') await api.post('/platform/portal/prospects', body);
            else await api.put(`/platform/portal/prospects/${editing.id}`, body);
            setEditing(null);
            toast.success(editing === 'new' ? 'Prospecto cargado' : 'Guardado', editing === 'new' ? 'BifeAI ya lo ve.' : undefined);
            await refresh();
          }}
        />
      )}
      {losing && (
        <LostDialog
          p={losing}
          onClose={() => setLosing(null)}
          onSave={async (reason) => {
            await api.post(`/platform/portal/prospects/${losing.id}/lost`, { reason });
            setLosing(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

export function PortalCommissionsPage() {
  const portal = usePortal();
  const r = useRange();
  const [tab, setTab] = useState<CommissionCircuit | 'PAYOUTS'>('OWN');
  const [status, setStatus] = useState<CommissionStatus | ''>('');
  const isLeader = portal.data?.seller.isLeader;
  const list = useQuery({
    queryKey: ['portal', 'commissions', tab, r.qs({ status }, 25)],
    queryFn: () => api.get<CommissionPageDTO>(`/platform/portal/commissions?${r.qs({ status, circuit: tab }, 25)}`),
    enabled: tab !== 'PAYOUTS',
  });
  const payouts = useQuery({
    queryKey: ['portal', 'payouts', r.qs()],
    queryFn: () => api.get<Paged<PayoutDTO>>(`/platform/portal/payouts?${r.qs()}`),
    enabled: tab === 'PAYOUTS',
  });
  const d = list.data;
  const pd = payouts.data;

  return (
    <>
      <PageHeader title="Comisiones" subtitle="Todo en dólares. Si te pagan en pesos, se usa el dólar del día del pago." />
      <div className="bf-view-switch" role="tablist" aria-label="Vista" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === 'OWN'} onClick={() => setTab('OWN')}>
          Mis ventas
        </button>
        {isLeader && (
          <button type="button" role="tab" aria-selected={tab === 'TEAM'} onClick={() => setTab('TEAM')}>
            Ventas de mi red
          </button>
        )}
        {portal.data?.seller.territoryRatePct != null && (
          <button type="button" role="tab" aria-selected={tab === 'TERRITORY'} onClick={() => setTab('TERRITORY')}>
            Mi territorio
          </button>
        )}
        <button type="button" role="tab" aria-selected={tab === 'PAYOUTS'} onClick={() => setTab('PAYOUTS')}>
          Pagos recibidos
        </button>
      </div>
      {tab !== 'PAYOUTS' && d && (
        <div className="bf-kpi-grid" style={{ marginBottom: 18 }}>
          <Kpi label="A cobrar" tone="amber" icon={<Clock />} value={formatUsd(d.totals.pendingCents)} />
          <Kpi label="Cobrado" tone="green" icon={<CircleDollarSign />} value={formatUsd(d.totals.paidCents)} />
          <Kpi label="Anulado" icon={<XCircle />} value={formatUsd(d.totals.canceledCents)} delta="Ventas devueltas" deltaMuted />
        </div>
      )}
      <Card>
        <CardHeader
          title={
            <RangeFilter r={r}>
              {tab !== 'PAYOUTS' && (
                <Select
                  aria-label="Estado"
                  style={{ width: 'auto' }}
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as CommissionStatus | '');
                    r.setPage(1);
                  }}
                >
                  <option value="">Todos los estados</option>
                  {COMMISSION_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {COMMISSION_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              )}
            </RangeFilter>
          }
        />
        <CardBody flush>
          {tab === 'PAYOUTS' ? (
            payouts.isPending ? (
              <Skeleton style={{ height: 160 }} />
            ) : !pd?.items.length ? (
              <EmptyState icon={<Wallet />} title="Todavía no hay pagos" />
            ) : (
              <PayoutsTable items={pd.items} />
            )
          ) : list.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !d?.items.length ? (
            <EmptyState icon={<BadgeDollarSign />} title="Sin comisiones" text="Cuando cierres una venta, tu comisión aparece acá." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    {tab !== 'OWN' && <th>Vendió</th>}
                    <th style={{ textAlign: 'right' }}>Venta</th>
                    <th style={{ textAlign: 'right' }}>Comisión</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((c) => (
                    <tr key={c.id}>
                      <td className="bf-cell-sub">{formatDate(c.createdAt)}</td>
                      <td>
                        <span className="bf-cell-main">{c.tenantName ?? '—'}</span>
                        <div className="bf-cell-sub">{ORDER_KIND_LABELS[c.orderKind]}</div>
                      </td>
                      {tab !== 'OWN' && (
                        <td className="bf-cell-sub">
                          {c.circuit === 'TERRITORY' ? 'Compra directa en tu territorio' : c.sourceSellerName}
                          {c.collectForTeam && <div>Es de {c.sellerName}: se la pagás vos</div>}
                        </td>
                      )}
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(c.baseCents)}
                        <div className="bf-cell-sub">{c.ratePct} %</div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        <strong>{formatUsd(c.amountCents)}</strong>
                      </td>
                      <td>
                        <CommissionStatusBadge status={c.status} />
                        {c.paidByLeader && <div className="bf-cell-sub">Te la paga {c.payeeName}</div>}
                        {c.paidOn && <div className="bf-cell-sub">{formatDate(c.paidOn)}</div>}
                        {c.canceledReason && <div className="bf-cell-sub">{c.canceledReason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {tab !== 'PAYOUTS' && d && <Pager page={d.page} total={d.total} pageSize={d.pageSize} onPage={r.setPage} />}
      {tab === 'PAYOUTS' && pd && <Pager page={pd.page} total={pd.total} pageSize={pd.pageSize} onPage={r.setPage} />}
    </>
  );
}

export function PortalTeamPage() {
  const team = useQuery({ queryKey: ['portal', 'team'], queryFn: () => api.get<TeamMemberSummaryDTO[]>('/platform/portal/team') });
  return (
    <>
      <PageHeader title="Mi red" subtitle="Tu gente (y la gente de tu gente), sus clientes y lo que ganás por sus ventas." />
      <Card>
        <CardBody flush>
          {team.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !team.data?.length ? (
            <EmptyState icon={<Users />} title="Todavía no tenés red" text="BifeAI suma a tus vendedores desde su panel." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th style={{ textAlign: 'right' }}>Su %</th>
                    <th style={{ textAlign: 'right' }}>Clientes</th>
                    <th style={{ textAlign: 'right' }}>Prospectos abiertos</th>
                    <th style={{ textAlign: 'right' }}>Vendido</th>
                    <th style={{ textAlign: 'right' }}>Lo que ganás vos</th>
                  </tr>
                </thead>
                <tbody>
                  {team.data.map((m) => (
                    <tr key={m.id} style={{ opacity: m.isActive ? 1 : 0.6 }}>
                      <td>
                        <span className="bf-cell-main" style={{ paddingLeft: (m.depth - 1) * 18 }}>
                          {m.depth > 1 ? '↳ ' : ''}
                          {m.name}
                        </span>{' '}
                        {!m.isActive && <Badge>De baja</Badge>}
                        {m.depth > 1 && (
                          <div className="bf-cell-sub" style={{ paddingLeft: (m.depth - 1) * 18 }}>
                            Debajo de {m.leaderName}
                          </div>
                        )}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {m.ratePct} %{m.teamRatePct !== null && <div className="bf-cell-sub">red {m.teamRatePct} %</div>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {m.clients}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {m.prospects}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(m.salesCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        <strong>{formatUsd(m.overrideCents)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

/** Cartera del partner: sus clientes (de su red y de su territorio) con estado, vencimiento y pagos. */
export function PortalClientsPage() {
  const list = useQuery({ queryKey: ['portal', 'clients'], queryFn: () => api.get<PortfolioClientDTO[]>('/platform/portal/clients') });
  const [q, setQ] = useState('');
  const rows = (list.data ?? []).filter((c) => !q || `${c.name} ${c.locality ?? ''} ${c.province ?? ''}`.toLowerCase().includes(q.toLowerCase()));
  const due = (d: number | null) =>
    d === null ? (
      <Badge>Sin fecha</Badge>
    ) : d < 0 ? (
      <Badge tone="danger">Vencido hace {-d} días</Badge>
    ) : d <= 30 ? (
      <Badge tone="warning">Vence en {d} días</Badge>
    ) : (
      <Badge tone="success">Al día</Badge>
    );
  return (
    <>
      <PageHeader title="Mis clientes" subtitle="Los clientes de tu red y de tu territorio: estado, vencimiento del mantenimiento y último pago." />
      <div style={{ maxWidth: 360, marginBottom: 14 }}>
        <Input placeholder="Buscar por nombre o zona" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar clientes" />
      </div>
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : list.isError ? (
            <EmptyState icon={<Store />} title="Tu acuerdo no incluye ver la cartera" />
          ) : !rows.length ? (
            <EmptyState icon={<Store />} title="Todavía no hay clientes" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Zona</th>
                    <th>Estado</th>
                    <th>Mantenimiento</th>
                    <th>Último pago</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.tenantId}>
                      <td>
                        <span className="bf-cell-main">{c.name}</span>
                        <div className="bf-cell-sub">{[c.ownerEmail, c.contactPhone].filter(Boolean).join(' · ') || '—'}</div>
                        <div className="bf-cell-sub">{c.sellerName ? `Vendió ${c.sellerName}` : 'Llegó por tu territorio'}</div>
                      </td>
                      <td className="bf-cell-sub">{[c.locality, c.province, COUNTRY_NAMES[c.country] ?? c.country].filter(Boolean).join(', ')}</td>
                      <td>
                        <Badge tone={c.licenseStatus === 'ACTIVE' ? 'success' : c.licenseStatus === 'SUSPENDED' ? 'danger' : 'warning'}>
                          {LICENSE_STATUS_LABELS[c.licenseStatus as keyof typeof LICENSE_STATUS_LABELS] ?? c.licenseStatus}
                        </Badge>
                        <div className="bf-cell-sub">{PLAN_LABELS[c.plan as Plan] ?? c.plan}</div>
                      </td>
                      <td>
                        {due(c.daysLeft)}
                        {c.serviceUntil && <div className="bf-cell-sub">{formatDate(c.serviceUntil)}</div>}
                        {c.manualBilling && <div className="bf-cell-sub">Cobro manual</div>}
                      </td>
                      <td className="bf-cell-sub">{c.lastPaidAt ? formatDate(c.lastPaidAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
